import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from 'reactstrap/lib/Button';
import classnames from 'classnames';

import { getTitleWithNoOrphans } from 'site-modules/shared/utils/get-title-with-no-orphans';
import { EngagementHandler } from 'client/engagement-handlers';
import { PrivatePartyEngagementHandler } from 'client/engagement-handlers/private-party-engagement-handler/private-party-engagement-handler';
import { EventToolbox } from 'client/utils/event-toolbox';

import { PRIVATE_PARTY_EVENTS } from 'site-modules/shared/constants/private-party/private-party-events';
import { CREATIVE_ID_HOMEPAGE_PP_BUTTON } from 'site-modules/shared/constants/private-party/private-party';

import { Link } from 'site-modules/shared/components/link/link';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ClippedContainer } from 'site-modules/shared/components/clipped-container/clipped-container';
import convexMaskMobile from 'site-modules/shared/components/clipped-container/mask-library/home-header-mobile';
import convexMaskDesktop from 'site-modules/shared/components/clipped-container/mask-library/convex-header';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import { GlobalSearch } from 'site-modules/shared/components/inventory/global-search/global-search';

import './home-hero-inventory.scss';

EngagementHandler.register(PrivatePartyEngagementHandler);

const BUTTON_DATA = [
  {
    id: 'used_btn',
    label: 'Shop Used',
    url: '/used-all/',
  },
  {
    id: 'new_btn',
    label: 'Shop New',
    url: '/new-cars-for-sale/',
  },
  {
    id: 'shop_for_sale_by_owner_btn',
    label: 'Shop For Sale By Owner',
    url: '/cars-for-sale-by-owner/',
  },
  {
    id: 'appraise_btn',
    label: 'Appraise My Car',
    url: '/appraisal/',
  },
  {
    id: 'list_car_for_sale_btn',
    label: 'List Your Car For Sale',
    url: '/sell-car/private/create-listing/',
  },
];

export function HomeHeroInventory({ trackingParent = null, isMobile = false }) {
  useEffect(() => {
    EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.WIDGET_VIEW, {
      creativeId: CREATIVE_ID_HOMEPAGE_PP_BUTTON,
    });
  }, []);

  const renderButtons = buttonList =>
    buttonList.map(({ id, label, url }) => (
      <Button
        key={id}
        tag={Link}
        color="outline-blue-50"
        to={url}
        className={classnames(
          'pos-r d-flex align-items-center text-transform-none inventory-button px-0_5 py-0 rounded-8 btn-bg-white',
          {
            'medium mb-1': isMobile,
            'size-16': !isMobile,
          }
        )}
        data-tracking-id="home_page_inventory_select_tab"
      >
        {label}
      </Button>
    ));

  const experimentParentDivClassnames = 'd-flex align-items-center justify-content-center buttons mb-1_5';
  const experimentTrackingParent = 'edm-entry-inventory-search';

  return (
    <FeatureFlag name="disable-homepage-llm">
      {disableLlm => {
        const convexMask = (
          <ClippedContainer
            containerClasses={classnames('homepage-gradient d-flex pos-a top-0 left-0 right-0', {
              mobile: isMobile,
            })}
            mask={isMobile ? convexMaskMobile : convexMaskDesktop}
          />
        );

        const header = (
          <ContentFragment
            componentToUse="h1"
            classes={classnames('text-white text-center pos-r pt-1_5 pb-1_5 mb-0 title pt-md-3', {
              desktop: !isMobile,
            })}
          >
            {getTitleWithNoOrphans('Let’s find your perfect car')}
          </ContentFragment>
        );

        return (
          <div className="home-hero-inventory pos-r p-0" data-tracking-parent={trackingParent}>
            <div className="pos-r px-1">
              {convexMask}
              {header}
              <GlobalSearch isMobile={isMobile} withLlmSearchDisabled={disableLlm} closeOnResultSelect />
            </div>
            <div
              className={classnames(experimentParentDivClassnames, {
                'mobile flex-wrap': isMobile,
                desktop: !isMobile,
              })}
              data-tracking-parent={experimentTrackingParent}
            >
              {renderButtons(BUTTON_DATA)}
            </div>
          </div>
        );
      }}
    </FeatureFlag>
  );
}

HomeHeroInventory.propTypes = {
  trackingParent: PropTypes.string,
  isMobile: PropTypes.bool,
};
